import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notificationsKeys } from '../../../api/notifications';
import { useAuth } from '../../../hooks/useAuth';
import { usePushNotifications } from '../../../hooks/usePushNotifications';
import { startForegroundPushToasts } from '../../../lib/foreground-push';
import { registerMessagingSw } from '../../../lib/firebase-messaging';

/**
 * Reenvia o token FCM ao backend quando o usuário já concedeu permissão
 * (ex.: refresh da página). O pedido de permissão fica no gesto do drawer.
 */
export function PushRegistration() {
  const { authUser } = useAuth();
  const queryClient = useQueryClient();
  const { isSupported, register, permission } = usePushNotifications();
  const registeredRef = useRef(false);

  useEffect(() => {
    if (!isSupported) return;
    startForegroundPushToasts();
    void registerMessagingSw();
  }, [isSupported]);

  useEffect(() => {
    if (!authUser || !isSupported || permission !== 'granted') return;

    let cancelled = false;

    const run = async () => {
      if (registeredRef.current) return;
      if (cancelled) return;

      registeredRef.current = true;
      try {
        const ok = await register();
        if (!ok) {
          registeredRef.current = false;
          return;
        }
        await queryClient.invalidateQueries({ queryKey: notificationsKeys.preferences() });
      } catch {
        registeredRef.current = false;
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [authUser, isSupported, permission, register, queryClient]);

  return null;
}
